import type { VirtualService } from '@app/modules/events/models/virtual-service.interface'
import { CreateEventAppPayload, EventAppLocationDetails, EventAppService } from '../types/event-app.type'

export const HANDSHAKE_SLUG = 'handshake' as const
export const SNOBALL_SLUG = 'snoball' as const

/**
 * Generates a unique suffix for the subcheckin.
 * @returns {string} - The unique suffix.
 */
export function uniqueSubcheckinSuffix(): string {
  return Math.random().toString(36).substring(2, 14)
}

/**
 * Maps catalog metadata to the API v3 service object stored under `location_details`.
 * @param service {VirtualService} - The virtual service to map.
 * @returns {EventAppService} - The mapped event app service.
 */
export function eventAppServiceFromVirtualService(service: VirtualService): EventAppService {
  const eventApp: EventAppService = {
    id: service.id,
    logo: service.logo,
    slug: service.slug,
    regex: [],
    title: service.title,
    active: service.active ?? false,
    website: service.website ?? '',
    zone_id: service.zone_id ?? null,
    settings: [],
    placeholder: service.placeholder ?? '',
    addDisplayText: service.addDisplayText,
    tutorialVideoId: service.tutorialVideoId ?? '',
    requiresApproval: service.requiresApproval,
    informationTemplate: service.informationTemplate
  }
  if (service.coverImageUrl) eventApp.coverImageUrl = service.coverImageUrl
  if (service.description) eventApp.description = service.description
  if (service.slackServiceName) eventApp.slackServiceName = service.slackServiceName
  if (service.has_integration != null) eventApp.has_integration = service.has_integration
  return eventApp
}

/**
 * Creates an empty event app location details object.
 * @param service {VirtualService} - The virtual service to create the location details for.
 * @returns {EventAppLocationDetails} - The empty event app location details.
 */
export function emptyEventAppLocationDetails(service: VirtualService): EventAppLocationDetails {
  return { link: '', service: eventAppServiceFromVirtualService(service) }
}

/**
 * Build create payload; slug/name rules match legacy virtual-subzones (Handshake vs `slug-randomId`).
 * @param catalog {readonly VirtualService[]} - The catalog of virtual services.
 * @param service {VirtualService} - The virtual service to create the payload for.
 * @returns {CreateEventAppPayload} - The create event app payload.
 */
export function buildCreateEventAppPayload(catalog: readonly VirtualService[], service: VirtualService): CreateEventAppPayload {
  const handshake = catalog.find((catalogService) => catalogService.slug === HANDSHAKE_SLUG)
  const isHandshake = handshake != null && service.id === handshake.id
  const isSnoball = service.slug === SNOBALL_SLUG
  const slug = isHandshake || isSnoball ? service.slug : `${service.slug}-${uniqueSubcheckinSuffix()}`
  const name = isHandshake ? 'Launch handshake app' : isSnoball ? 'Snöball' : 'Session/Title'
  return {
    name,
    slug,
    location_details: emptyEventAppLocationDetails(service)
  }
}
